/**
 * ファイルサイズを人間が読みやすい形式に変換します
 * @param sizeInBytes バイト単位のファイルサイズ
 * @returns 整形されたファイルサイズ文字列（例：2.5 MB）
 */
export const formatFileSize = (sizeInBytes: number): string => {
  if (sizeInBytes < 1024) {
    return `${sizeInBytes} B`;
  } else if (sizeInBytes < 1024 * 1024) {
    return `${(sizeInBytes / 1024).toFixed(2)} KB`;
  } else {
    return `${(sizeInBytes / (1024 * 1024)).toFixed(2)} MB`;
  }
};

/**
 * エラーメッセージの英語→日本語マッピング
 */
const ERROR_MESSAGE_MAP: Record<string, string> = {
  // パスワード検証エラー
  'Old password is incorrect': 'パスワードが間違っています',
  
  // 入力バリデーションエラー
  'oldPassword and newPassword are required': '現在のパスワードと新しいパスワードを入力してください',
  '現在のパスワードを入力してください': '現在のパスワードを入力してください',
  '新しいパスワードは6文字以上で入力してください': '新しいパスワードは6文字以上で入力してください',
  
  // その他のエラー
  'User not found': 'ユーザーが見つかりません',
  'Failed to change password': '予期せぬエラーでパスワードが変更できませんでした。管理者にお問い合わせください',
  'バリデーションエラー': 'バリデーションエラー', // zodエラーの場合はdetailsを優先
};

/**
 * エラーメッセージを日本語に翻訳します
 * @param errorMsg 英語のエラーメッセージ
 * @returns 日本語のエラーメッセージ
 */
const translateErrorMessage = (errorMsg: string): string => {
  return ERROR_MESSAGE_MAP[errorMsg] || errorMsg;
};

/**
 * エラーメッセージを標準化します
 * @param error エラーオブジェクト
 * @returns 標準化されたエラーメッセージ
 */
export const getErrorMessage = (error: unknown): string => {
  // axiosエラーの場合
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as { response?: { data?: { error?: string; message?: string; details?: Array<{ path: string; message: string }> } } };
    
    // zodバリデーションエラーの場合
    if (axiosError.response?.data?.details && Array.isArray(axiosError.response.data.details)) {
      const firstError = axiosError.response.data.details[0];
      if (firstError?.message) {
        return translateErrorMessage(firstError.message);
      }
    }
    
    // バックエンドのエラーメッセージを優先
    if (axiosError.response?.data?.error) {
      // 英語メッセージを日本語に変換
      const errorMsg = axiosError.response.data.error;
      return translateErrorMessage(errorMsg);
    }
    
    if (axiosError.response?.data?.message) {
      return axiosError.response.data.message;
    }
  }
  
  // 通常のErrorオブジェクトの場合
  if (error instanceof Error) {
    return error.message;
  }
  
  // 文字列の場合
  if (typeof error === 'string') {
    return error;
  }
  
  return '予期せぬエラーでパスワードが変更できませんでした。管理者にお問い合わせください';
}; 
